/**
 * Jobs List Component for Compute Jobs Page
 */

import React, { useContext, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { fetchComputeJobs } from '../../middleware/api/compute_job_apis';
import ToolButton, { ToolButtonType } from '../../tools/tool-button';
import PleaseWait from '../../tools/please-wait';
import './jobs-list.css';

// types
import { ComputeJob } from '../compute-jobs/types';
import { Event, EventType, EventTarget } from '../../utils/events';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { fnD_getComputeJobs } from '../../app/data-actions';

interface JobsListProps {
  setEventHandler: (handler: any) => void;
}

/**
 * Get status badge class based on job status
 */
const getStatusBadgeClass = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower === 'running') return 'status-running';
  if (statusLower === 'completed') return 'status-completed';
  if (statusLower === 'failed' || statusLower === 'error') return 'status-failed';
  if (statusLower === 'pending') return 'status-pending';
  return 'status-unknown';
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

export default function JobsList(props: JobsListProps) {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data state variables.
  const sD_computeJobs: ComputeJob[] = useAppSelector((state) => state.data.computeJobs);
  const sD_computeJobsLoading: boolean = useAppSelector(
    (state) => state.data.computeJobsLoading,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const [error, setError] = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  const eventHandler = (args: { event: Event }) => {
    console.log('JobsList eventHandler:', args.event);
  };

  const handleRowClick = (jobId: string) => {
    setSelectedJobId(jobId === selectedJobId ? null : jobId);
  };

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  useEffect(() => {
    props.setEventHandler(eventHandler);
  }, []);

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  if (error) {
    return (
      <div className="jobs-list-error">
        <p>{error}</p>
        <ToolButton
          name="retry-button"
          type={ToolButtonType.PRIMARY}
          onClick={() => dispatch(fnD_getComputeJobs())}
          tooltip="Retry"
          text="Retry"
        />
      </div>
    );
  }

  if (sD_computeJobs.length === 0 && !sD_computeJobsLoading) {
    return (
      <div className="no-data-overlay">
        <p>No compute jobs</p>
      </div>
    );
  }

  return (
    <div className="jobs-list-container">
      <PleaseWait text="Loading jobs....." visible={sD_computeJobsLoading} />

      {/*<div className="jobs-list-table-wrapper">*/}
      <table className="jobs-list-table">
        <thead>
          <tr>
            <th>Job Name</th>
            <th>Job ID</th>
            <th>Container</th>
            <th>Status</th>
            <th>Submitted</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {sD_computeJobs.map((job) => (
            <tr
              key={job.job_id}
              className={selectedJobId === job.job_id ? 'selected' : ''}
              onClick={() => handleRowClick(job.job_id)}
            >
              <td className="job-name-cell" title={job.job_name}>
                {job.job_name}
              </td>
              <td className="job-id-cell" title={job.job_id}>
                {job.job_id}
              </td>
              <td
                className="container-cell"
                title={job.submission_params.container_image}
              >
                {job.submission_params.container_image.split('/').pop()?.split(':')[0] ||
                  job.submission_params.container_image}
              </td>
              <td className="status-cell">
                <span className={`status-badge ${getStatusBadgeClass(job.status)}`}>
                  {job.status}
                </span>
              </td>
              <td className="timestamp-cell">{formatTimestamp(job.submitted_at)}</td>
              <td className="timestamp-cell">{formatTimestamp(job.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/*</div>*/}
    </div>
  );
}
